import { useMemo } from 'react';
import { useCurrentLocale } from '@/hooks/useCurrentLocale';

/**
 * Get localized country name using browser's Intl.DisplayNames API
 * Works offline - uses browser's built-in localization data
 * 
 * @param isoCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'BR', 'DE')
 * @param locale - Target locale (e.g., 'en-US', 'pt-BR', 'de-DE')
 * @returns Localized country name or the ISO code as fallback
 */
export function getCountryDisplayName(isoCode: string | null | undefined, locale: string): string {
  if (!isoCode) return '';
  
  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    return displayNames.of(isoCode.toUpperCase()) || isoCode;
  } catch {
    return isoCode;
  }
}

/**
 * Get localized language name using browser's Intl.DisplayNames API
 * Works offline - uses browser's built-in localization data
 * 
 * @param localeCode - Full locale code (e.g., 'pt-BR', 'en-US', 'de-DE')
 * @param uiLocale - User's UI language for display (e.g., 'en-US', 'pt-BR')
 * @returns Localized language name or the locale code as fallback
 */
export function getLanguageDisplayName(localeCode: string | null | undefined, uiLocale: string): string {
  if (!localeCode) return '';
  
  try {
    const displayNames = new Intl.DisplayNames([uiLocale], { 
      type: 'language',
      languageDisplay: 'standard'  // Forces "English (United States)" instead of "American English"
    });
    return displayNames.of(localeCode) || localeCode;
  } catch {
    return localeCode;
  }
}

/**
 * Get localized timezone name using browser's Intl.DateTimeFormat API
 * Works offline - uses browser's built-in localization data
 * 
 * @param timezoneId - IANA timezone ID (e.g., 'America/New_York')
 * @param locale - User's locale for display
 * @returns Localized timezone name (e.g., "Eastern Standard Time")
 */
export function getLocalizedTimezoneName(timezoneId: string, locale: string): string {
  if (!timezoneId) return '';
  
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: timezoneId,
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || timezoneId.replace(/_/g, ' ');
  } catch {
    return timezoneId.replace(/_/g, ' ');
  }
}

/**
 * Get GMT offset for a timezone (e.g., "GMT-5", "GMT+5:30")
 * Uses browser's Intl API - works offline
 * 
 * @param timezoneId - IANA timezone ID (e.g., 'America/New_York')
 * @returns GMT offset string (e.g., "GMT-5", "GMT+5:30")
 */
export function getTimezoneGMTOffset(timezoneId: string): string {
  if (!timezoneId) return 'GMT';
  
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneId,
      timeZoneName: 'shortOffset'
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    return offsetPart?.value || 'GMT';
  } catch {
    return 'GMT';
  }
}

/**
 * Get numeric offset in minutes for a timezone (for sorting)
 * Positive = east of UTC, Negative = west of UTC
 * 
 * @param timezoneId - IANA timezone ID (e.g., 'America/New_York')
 * @returns Offset in minutes (e.g., -300 for GMT-5, 330 for GMT+5:30)
 */
export function getTimezoneOffsetMinutes(timezoneId: string): number {
  if (!timezoneId) return 0;
  
  try {
    const now = new Date();
    // Get UTC time and timezone time to calculate offset
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezoneId }));
    return Math.round((tzDate.getTime() - utcDate.getTime()) / 60000);
  } catch {
    return 0;
  }
}

/**
 * Get currency symbol using browser's Intl.NumberFormat API
 * Works offline - uses browser's built-in localization data
 * 
 * @param currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR')
 * @param locale - User's locale for formatting
 * @returns Currency symbol (e.g., '$', '€', '¥')
 */
export function getCurrencySymbol(currencyCode: string, locale: string = 'en-US'): string {
  if (!currencyCode) return '';
  
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const formatted = formatter.format(0);
    // Remove digits and spaces to get the symbol
    return formatted.replace(/[\d\s.,]/g, '').trim() || currencyCode;
  } catch {
    return currencyCode;
  }
}

/**
 * React hook to get a localized country name based on user's navigation language
 * Uses i18n.language which is detected synchronously from localStorage/navigator
 * 
 * @param isoCode - ISO 3166-1 alpha-2 country code
 * @returns Localized country name
 */
export function useCountryName(isoCode: string | null | undefined): string {
  const locale = useCurrentLocale();
  return useMemo(() => {
    if (!isoCode) return '';
    return getCountryDisplayName(isoCode, locale);
  }, [isoCode, locale]);
}
